<script setup lang="ts">
import dayjs, { type Dayjs } from "dayjs";
import { useCalendarStore, type ICalendarEvent } from "@/store/calendar";
import { editClassRequest, openBookingModal } from "@/assets/js/scenario2";
import { useSessionStore } from "@/store/session";

import type { TSchoolCalendarFiltersType } from "@/assets/js/globalTypes";
import {
	cancelClassModal,
	openEditNoShowModal,
} from "@/components/modals/modalDefinitions";
import api from "@/api";
import { useToastStore } from "@/store/toast";
import { useClassStore } from "@/store/class";
import { useIsMobile } from "@/assets/js/helpers";
import MdiViewAgendaOutline from "~icons/mdi/view-agenda-outline";
import MdiFilterOutline from "~icons/mdi/filter-outline";
import MdiPlusCircleOutline from "~icons/mdi/plus-circle-outline";
import { $t } from "@/i18n";
import {
	showAgendaPopover,
	showSchoolFiltersPopover,
} from "@/components/popovers/popoverDefinitions";
import { useSchoolStore } from "@/store/school";

definePage({
	name: "school.class-management.calendar",
});

const filters = ref<TSchoolCalendarFiltersType | null>(null);
const schoolStore = useSchoolStore();
const calendarStore = useCalendarStore();
const sessionStore = useSessionStore();
const isMobile = useIsMobile();
// const usrId = sessionStore.userId;
const schoolId = sessionStore.userData.scl_id;
const currentDate = ref<Dayjs>(dayjs());
const isLoading = ref(true);
const teacherIds = ref<number[]>([]);
const programIds = ref<number[]>([]);
const statuses = ref<string[]>(["COMPLETED", "PENDING"]);
const teacherMenuOptions = [
	{
		prependIcon: MdiViewAgendaOutline,
		label: $t("app.teacher.dashboard.calendar.mobileMenuAgenda") as string,
		onClick: () =>
			showAgendaPopover({
				events: calendarStore.getAllEvents,
				onDeleteEvent: (e) => cancelClass(e),
				onEdit: (e) => editClass(e),
			}),
	},
	{
		prependIcon: MdiFilterOutline,
		label: $t("app.teacher.dashboard.calendar.mobileMenuFilters") as string,
		onClick: () =>
			showSchoolFiltersPopover({
				filters: filters.value,
				onFilterChange: (e) => filterChange(e),
			}),
	},
	{
		prependIcon: MdiPlusCircleOutline,
		label: $t("app.school.classManagement.createNewCourse") as string,
		onClick: () => {
			openBookingModal();
		},
	},
];

watch(
	() => calendarStore.stateRefreshHandle,
	async () => {
		setCalendarState();
		await fetchInfo();
	},
);

const toastStore = useToastStore();

function cancelClass(classObj: ICalendarEvent) {
	cancelClassModal({
		id: classObj.id,
		onBack: async () => {
			await fetchInfo();
		},
		onCancelClassRequest: async (classData: { id: number; clr_id: number }) => {
			try {
				const res = await api.cancelClass(classData);
				// classStore.removeClass(classObj);
				toastStore.openToastSuccess(res.data.message);
			} catch (err) {
				toastStore.openToastError(err.response?.data?.message || err.message);
			} finally {
				await fetchInfo();
			}
		},
	});
}

async function editClass(event: ICalendarEvent) {
	if (event.additionalData.cls_status === "COMPLETED") {
		openEditNoShowModal({
			onNext: async (val: boolean) => {
				if (!val) {
					return;
				}
				try {
					const res = await api.editClassRequest({
						crq_id: event.additionalData.crq_id,
						classes: [
							{
								cls_id: event.id,
								cls_status: "NO-SHOW",
							},
						],
					});
					toastStore.openToastSuccess(res.data.message);
				} catch (err) {
					toastStore.openToastError(err.response?.data?.message || err.message);
				} finally {
					await fetchInfo();
				}
			},
		});
	} else if (
		event.additionalData.crq_status === "DRAFT" ||
		event.additionalData.crq_status === "ACCEPTED" ||
		event.additionalData.crq_status === "OPEN"
	) {
		const dateObject = {
			from: "",
			to: "",
		};

		if (event.additionalData.crq_status === "ACCEPTED") {
			dateObject.from = calendarStore.getActiveDate
				.startOf("month")
				.format("YYYY-MM-DD");
			dateObject.to = calendarStore.getActiveDate
				.endOf("month")
				.format("YYYY-MM-DD");

			calendarStore.fetchTeacherAvailability(
				event.additionalData.usr_id_teacher,
				{
					avl_start: dateObject.from,
					avl_end: dateObject.to,
				},
				"month",
			);
		}

		try {
			const res = await api.getSchoolClassRequest(event.additionalData.crq_id);
			// console.log(res);
			const data = res.data.data;
			classStore.loadClassRequest(data);
			editClassRequest(
				event,
				event.additionalData.crq_status === "ACCEPTED",
				dayjs(dateObject.from),
			);
		} catch (error) {
			console.error(error);
		}
	}
}

const classStore = useClassStore();

// async function handleFindTeacher(id: number) {
// 	try {
// 		const res = await api.getSchoolClassRequest(id);
// 		// console.log(res);
// 		const data = res.data.data;
// 		classStore.loadClassRequest(data);
// 		continueDraft(dayjs(data.from));
// 	} catch (error) {
// 		console.error(error);
// 	}
// }
function setCalendarState() {
	calendarStore.setIsAdmin(true);
	calendarStore.setDayClickHandler(null);
	calendarStore.setDeleteEventHandler(cancelClass);
	calendarStore.setEditEvent(editClass);
	calendarStore.setIsPreview(false);
}

onBeforeMount(() => {
	setCalendarState();
});

async function fetchInfo(isInitial: boolean = false) {
	isLoading.value = true;
	const dateObject = {
		from: "",
		to: "",
	};

	dateObject.from = calendarStore.getActiveDate
		.startOf("month")
		.format("YYYY-MM-DD");
	dateObject.to = calendarStore.getActiveDate
		.endOf("month")
		.format("YYYY-MM-DD");

	await calendarStore.fetchSchoolEvents(schoolId, {
		date_from: dateObject.from,
		date_to: dateObject.to,
		usr_ids: teacherIds.value.join(","),
		tags: programIds.value.join(","),
		cls_status: statuses.value ? toRaw(statuses.value).join(",") : null,
	});
	isLoading.value = false;

	await getCalendarFilters(isInitial);
}

async function getCalendarFilters(isInitial: boolean = true) {
	try {
		const res = await api.getCalendarFilters({ scl_id: schoolId });

		if (isInitial) {
			filters.value = res.data.data;

			const programs =
				filters.value.programs?.map((program) => ({
					tag_id: program.tag_id,
					tag_name: program.tag_name,
					checked: false, // Set initial checked value to false
				})) || [];

			const teachers =
				filters.value.teachers?.map((teacher) => ({
					usr_id: teacher.usr_id,
					usr_fullname: teacher.usr_fullname,
					checked: false, // Set initial checked value to false
				})) || [];

			schoolStore.schoolSelectedCalendarFilters = {
				programs,
				teachers,
			};
		} else {
			const currentFilters = schoolStore.schoolSelectedCalendarFilters;

			const newPrograms = res.data.data?.programs || [];
			const updatedPrograms = newPrograms.map((newProgram: any) => {
				const existing = currentFilters.programs?.find(
					(p) => p.tag_id === newProgram.tag_id,
				);
				return {
					...newProgram,
					checked: existing?.checked ?? false,
				};
			});
			const newTeachers = res.data.data?.teachers || [];
			const updatedTeachers = newTeachers.map((newTeacher: any) => {
				const existing = currentFilters.teachers?.find(
					(t) => t.usr_id === newTeacher.usr_id,
				);
				return {
					...newTeacher,
					checked: existing?.checked ?? false,
				};
			});
			schoolStore.schoolSelectedCalendarFilters = {
				programs: updatedPrograms,
				teachers: updatedTeachers,
			};
		}
	} catch (error) {
		console.error(error);
	}
}

onMounted(async () => {
	await fetchInfo(true);
});

const handleDayClick = async (day: Dayjs) => {
	calendarStore.setActiveDate(day);
	await fetchInfo();
};

const handleNavClick = async (direction: number, event?: any) => {
	if (!event) event = calendarStore.getView;
	if (event) {
		if (event === "week") {
			calendarStore.setActiveDate(
				direction > 0
					? calendarStore.getActiveDate.add(7, "day")
					: calendarStore.getActiveDate.subtract(7, "day"),
			);
		} else if (event === "month") {
			calendarStore.setActiveDate(
				calendarStore.getActiveDate.add(direction, "month"),
			);
		}
		await fetchInfo();
		return;
	}
	calendarStore.setActiveDate(
		calendarStore.getActiveDate.add(direction, "month"),
	);
	await fetchInfo();
};

onBeforeUnmount(() => {
	calendarStore.setIsAdmin(false);
	calendarStore.setDeleteEventHandler(null);
	calendarStore.setEditEvent(null);
	calendarStore.setDayClickHandler(null);
});

async function filterChange(e: TSchoolCalendarFiltersType) {
	teacherIds.value = e.teachers.map((teacher) => teacher.usr_id);
	programIds.value = e.programs.map((program) => program.tag_id);
	statuses.value = e.statuses;

	await fetchInfo();
}
</script>

<template>
	<div id="timetables-page">
		<div
			v-if="!isMobile"
			class="calendar-page-sidebar"
		>
			<modern-calendar
				:current-date="currentDate"
				@day-click="handleDayClick"
				@nav-click="handleNavClick"
			/>
			<p-button
				:pt="{
					root: {
						class: 'calendar-md-button',
					},
				}"
				:disabled="!schoolStore.isSubActive"
				@click="openBookingModal"
			>
				<i-mdi-calendar-check-outline />
				{{ $t("app.school.classManagement.createNewCourse") }}
			</p-button>
			<calendar-filters-school
				@filter-change="filterChange($event as TSchoolCalendarFiltersType)"
			/>
		</div>
		<div class="calendar-wrap">
			<nice-calendar
				v-if="!isLoading"
				:hide-view="'week'"
				@next="handleNavClick(1, $event)"
				@prev="handleNavClick(-1, $event)"
			/>
			<p-progress-spinner
				v-else
				class="calendar-progress-spinner"
			></p-progress-spinner>
			<!-- @refresh:week="handleRefreshWeek"
				@refresh:date="handleRefreshDate"
				@refresh:view="handleRefreshView" -->
		</div>
		<mobile-menu
			v-if="isMobile"
			:menu-items="teacherMenuOptions"
		/>
	</div>
</template>

<style lang="scss" scoped>
#timetables-page {
	display: flex;
	padding-top: 20px;

	.calendar-page-sidebar {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 10px;
	}

	.calendar-wrap {
		flex: 1;
		padding: 10px 10px 50px;
		position: relative;
	}
}
</style>
